import { useEffect, useMemo, useRef, useState, type ChangeEvent, type ClipboardEvent, type FormEvent } from 'react';
import { formatBp, simularTodas, type TabelaTaxas } from '@agencia/shared';
import { useCurrentUser } from '@/auth/AuthContext';
import { LinkPagamento } from '@/components/LinkPagamento';
import { Alert, Button } from '@/components/ui';
import type { Capabilities } from '@/lib/crm';
import { api } from '@/lib/api';
import { gerarArte, loadImage } from '@/lib/arte';
import { errorMessage } from '@/lib/errors';
import { formatBRL, formatDate, parseMoneyInput } from '@/lib/format';
import { useApi } from '@/lib/useApi';

type Taxas = TabelaTaxas & { atualizadaPor: string | null; padrao: boolean };
type Aba = 'taxas' | 'arte' | 'tabela';

/** Simulador de taxas do Mercado Pago + gerador de arte com o voo e as parcelas. */
export function SimulatorPage() {
  const me = useCurrentUser();
  const { data: taxas, error, reload } = useApi<Taxas>('/settings/mp-fees');
  const [aba, setAba] = useState<Aba>('taxas');

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <h1 className="page-title">Simulador</h1>
          <p className="muted">
            Taxas do Mercado Pago (link de pagamento, cartão de crédito)
            {taxas && <> · conferidas em {formatDate(taxas.conferidaEm)}</>}
          </p>
        </div>
      </header>
      <div className="tabs" role="tablist">
        <button role="tab" aria-selected={aba === 'taxas'} className="tab" onClick={() => setAba('taxas')}>
          Taxas
        </button>
        <button role="tab" aria-selected={aba === 'arte'} className="tab" onClick={() => setAba('arte')}>
          Arte para o cliente
        </button>
        {me.role === 'ADMIN' && (
          <button role="tab" aria-selected={aba === 'tabela'} className="tab" onClick={() => setAba('tabela')}>
            Tabela de taxas
          </button>
        )}
      </div>
      {error && <Alert>{error}</Alert>}
      {!taxas ? (
        !error && <p className="muted">Carregando…</p>
      ) : aba === 'taxas' ? (
        <TaxasTab tabela={taxas} />
      ) : aba === 'arte' ? (
        <ArteTab tabela={taxas} />
      ) : (
        <TabelaTab tabela={taxas} onSaved={() => void reload()} />
      )}
    </div>
  );
}

function ValorInput({ value, onChange, id }: { value: string; onChange: (v: string) => void; id: string }) {
  return (
    <div className="field">
      <label htmlFor={id}>{id === 'arte-valor' ? 'Valor à vista (PIX)' : 'Valor da venda'}</label>
      <input id={id} className="big-input" inputMode="decimal" placeholder="R$ 0,00" value={value} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}

function TaxasTab({ tabela }: { tabela: Taxas }) {
  const [raw, setRaw] = useState('1.000,00');
  const valor = parseMoneyInput(raw);
  const { data: caps } = useApi<Capabilities>('/crm/capabilities');
  // Tabela fixa para consulta: para cada parcela, quanto cobrar para a agência receber o valor digitado.
  const linhas = useMemo(() => {
    if (!valor || Number(valor) <= 0) return null;
    try {
      return simularTodas(valor, true, tabela).map((c) => ({ c }));
    } catch {
      return null;
    }
  }, [valor, tabela]);

  return (
    <div className="sim">
      <section className="card">
        <ValorInput id="sim-valor" value={raw} onChange={setRaw} />
        {linhas ? (
          <div className="sim-table">
            <table>
              <thead>
                <tr>
                  <th>Parcelas</th>
                  <th className="num">Taxa</th>
                  <th className="num">Cobrar do cliente</th>
                  <th className="num">Parcela</th>
                  <th className="num">Agência recebe</th>
                </tr>
              </thead>
              <tbody>
                {linhas.map(({ c }) => (
                  <tr key={c.parcelas}>
                    <td>{c.parcelas === 1 ? 'À vista' : `${c.parcelas}x`}</td>
                    <td className="num">{formatBp(c.taxaTotalBp)}</td>
                    <td className="num strong">{formatBRL(c.clientePaga)}</td>
                    <td className="num">{c.parcelas === 1 ? '—' : formatBRL(c.valorParcela)}</td>
                    <td className="num">{formatBRL(c.voceRecebe)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="muted small">
              Cobrando o valor da coluna "Cobrar do cliente", a agência recebe o valor digitado ({formatBRL(valor ?? '0')}), com diferença de centavos pelo
              arredondamento da parcela. Taxas conferidas em {formatDate(tabela.conferidaEm)}.
            </p>
          </div>
        ) : (
          <p className="muted">Digite o valor da venda.</p>
        )}
      </section>
      {caps?.mercadoPago && valor && Number(valor) > 0 && (
        <aside className="card sim__result">
          <LinkPagamento key={valor} valorPix={valor} embutirTaxasPadrao mostrarTabela={false} />
        </aside>
      )}
    </div>
  );
}

const MAX_FOTOS = 3;
const ROTULOS_PADRAO = ['Ida', 'Volta', 'Hotel'];

function ArteTab({ tabela }: { tabela: Taxas }) {
  const [fotos, setFotos] = useState<{ id: number; img: HTMLImageElement; rotulo: string }[]>([]);
  const [raw, setRaw] = useState('');
  const [titulo, setTitulo] = useState('');
  const [mostrarPix, setMostrarPix] = useState(true);
  const [png, setPng] = useState<{ url: string; blob: Blob } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const seq = useRef(0);
  const { data: caps } = useApi<Capabilities>('/crm/capabilities');

  // Libera a imagem anterior da memória quando uma nova é gerada.
  useEffect(() => {
    return () => {
      if (png) URL.revokeObjectURL(png.url);
    };
  }, [png]);

  async function abrirArquivos(files: (File | null | undefined)[]) {
    const imgs = files.filter((f): f is File => Boolean(f));
    if (imgs.length === 0) return;
    setError(null);
    for (const file of imgs) {
      if (!file.type.startsWith('image/')) return setError('Envie uma imagem (print ou foto do voo).');
      if (file.size > 15 * 1024 * 1024) return setError('Imagem muito grande (máximo 15 MB).');
    }
    try {
      const novas = await Promise.all(imgs.map((f) => loadImage(URL.createObjectURL(f))));
      if (fotos.length + novas.length > MAX_FOTOS) setError(`No máximo ${MAX_FOTOS} fotos por arte. As primeiras foram usadas.`);
      setFotos((atual) => {
        const lista = [...atual];
        for (const img of novas) {
          if (lista.length >= MAX_FOTOS) break;
          lista.push({ id: ++seq.current, img, rotulo: '' });
        }
        // Com mais de uma foto, sugere "Ida" / "Volta" nas que ainda não têm nome.
        if (lista.length > 1) return lista.map((f, i) => (f.rotulo ? f : { ...f, rotulo: ROTULOS_PADRAO[i] ?? '' }));
        return lista;
      });
      setPng(null);
    } catch (err) {
      setError(errorMessage(err));
    }
  }

  function onPaste(e: ClipboardEvent) {
    const files = [...e.clipboardData.items].filter((i) => i.type.startsWith('image/')).map((i) => i.getAsFile());
    if (files.length) void abrirArquivos(files);
  }

  function mudarRotulo(id: number, rotulo: string) {
    setFotos((l) => l.map((f) => (f.id === id ? { ...f, rotulo } : f)));
    setPng(null);
  }

  function remover(id: number) {
    setFotos((l) => {
      const resto = l.filter((f) => f.id !== id);
      return resto.length === 1 && ROTULOS_PADRAO.includes(resto[0]!.rotulo) ? [{ ...resto[0]!, rotulo: '' }] : resto;
    });
    setPng(null);
  }

  function mover(id: number, dir: -1 | 1) {
    setFotos((l) => {
      const i = l.findIndex((f) => f.id === id);
      const j = i + dir;
      if (i < 0 || j < 0 || j >= l.length) return l;
      const n = [...l];
      [n[i], n[j]] = [n[j]!, n[i]!];
      return n;
    });
    setPng(null);
  }

  async function gerar(e: FormEvent) {
    e.preventDefault();
    const valor = parseMoneyInput(raw);
    if (fotos.length === 0) return setError('Escolha a foto do voo.');
    if (!valor || Number(valor) <= 0) return setError('Digite o valor da venda (ex.: 1.939,68).');
    setBusy(true);
    setError(null);
    try {
      const canvas = await gerarArte({ fotos: fotos.map((f) => ({ img: f.img, rotulo: f.rotulo })), valor, titulo: titulo.trim(), mostrarPix, tabela });
      const blob = await new Promise<Blob | null>((r) => canvas.toBlob(r, 'image/png'));
      if (!blob) throw new Error('Não foi possível gerar a imagem.');
      setPng({ url: URL.createObjectURL(blob), blob });
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  const arquivo = png ? new File([png.blob], 'fly-now-orcamento.png', { type: 'image/png' }) : null;
  const podeCompartilhar = Boolean(arquivo && navigator.canShare?.({ files: [arquivo] }));

  return (
    <div className="sim" onPaste={onPaste}>
      <form className="card" onSubmit={(e) => void gerar(e)}>
        <h2>Monte a arte</h2>
        <p className="muted small">A arte junta as fotos do voo, a logo da Fly Now, o valor no cartão em até 10x sem juros (taxas já incluídas) e o valor à vista no PIX.</p>
        {error && <Alert>{error}</Alert>}
        <div className="field">
          <label htmlFor="arte-foto">Fotos ou prints do voo (até {MAX_FOTOS})</label>
          {fotos.length > 0 && (
            <ul className="arte-fotos">
              {fotos.map((f, i) => (
                <li key={f.id} className="arte-foto">
                  <img src={f.img.src} alt={`Foto ${i + 1}`} />
                  <div className="arte-foto__info">
                    <input
                      aria-label={`Nome da foto ${i + 1}`}
                      placeholder={fotos.length > 1 ? 'Ex.: Ida · LATAM' : 'Etiqueta (opcional)'}
                      maxLength={40}
                      value={f.rotulo}
                      onChange={(e) => mudarRotulo(f.id, e.target.value)}
                    />
                    <div className="row tight">
                      {fotos.length > 1 && (
                        <>
                          <button type="button" className="btn btn--ghost btn--sm" disabled={i === 0} onClick={() => mover(f.id, -1)} aria-label="Subir">
                            ↑
                          </button>
                          <button type="button" className="btn btn--ghost btn--sm" disabled={i === fotos.length - 1} onClick={() => mover(f.id, 1)} aria-label="Descer">
                            ↓
                          </button>
                        </>
                      )}
                      <button type="button" className="btn btn--ghost btn--sm" onClick={() => remover(f.id)}>
                        Remover
                      </button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
          {fotos.length < MAX_FOTOS && (
            <button type="button" className={`dropzone ${fotos.length ? 'dropzone--small' : ''}`} onClick={() => fileRef.current?.click()}>
              <span>
                {fotos.length === 0 ? 'Toque para escolher a imagem ou cole (Ctrl+V)' : `+ Adicionar outra foto (ex.: voo de volta) · ou cole (Ctrl+V)`}
              </span>
            </button>
          )}
          <input
            ref={fileRef}
            id="arte-foto"
            type="file"
            accept="image/*"
            multiple
            className="sr-only"
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              void abrirArquivos([...(e.target.files ?? [])]);
              e.target.value = '';
            }}
          />
        </div>
        <ValorInput id="arte-valor" value={raw} onChange={setRaw} />
        <div className="field">
          <label htmlFor="arte-titulo">Título (opcional)</label>
          <input id="arte-titulo" placeholder="Ex.: Salvador ✈ Lisboa · ida e volta" maxLength={60} value={titulo} onChange={(e) => setTitulo(e.target.value)} />
        </div>
        <label className="check">
          <input type="checkbox" checked={mostrarPix} onChange={(e) => setMostrarPix(e.target.checked)} />
          Mostrar o valor à vista no PIX
        </label>
        <Button type="submit" loading={busy}>
          Gerar arte
        </Button>
      </form>
      <aside className="card arte-preview">
        {png ? (
          <>
            <img src={png.url} alt="Arte gerada para o cliente" />
            <div className="row">
              {podeCompartilhar && arquivo && (
                <Button onClick={() => void navigator.share({ files: [arquivo] }).catch(() => undefined)}>Enviar (WhatsApp)</Button>
              )}
              <a className={`btn ${podeCompartilhar ? 'btn--secondary' : 'btn--primary'}`} href={png.url} download="fly-now-orcamento.png">
                Baixar imagem
              </a>
            </div>
            {caps?.mercadoPago && <LinkPagamento key={png.url} valorPix={parseMoneyInput(raw)} descricaoInicial={titulo.trim()} embutirTaxasPadrao />}
          </>
        ) : (
          <p className="muted">A arte aparece aqui.</p>
        )}
      </aside>
    </div>
  );
}

function TabelaTab({ tabela, onSaved }: { tabela: Taxas; onSaved: () => void }) {
  const toPct = (bp: number) => (bp / 100).toFixed(2).replace('.', ',');
  const [venda, setVenda] = useState(toPct(tabela.taxaVendaBp));
  const [parc, setParc] = useState(tabela.parcelamentoBp.map(toPct));
  const [msg, setMsg] = useState<{ kind: 'error' | 'success'; text: string } | null>(null);
  const [saving, setSaving] = useState(false);
  const toBp = (s: string) => {
    const n = Number(s.replace('%', '').replace(',', '.').trim());
    return Number.isFinite(n) && n >= 0 && n < 50 ? Math.round(n * 100) : null;
  };

  async function salvar(e: FormEvent) {
    e.preventDefault();
    const vendaBp = toBp(venda);
    const parcBp = parc.map(toBp);
    if (vendaBp === null || parcBp.some((v) => v === null)) return setMsg({ kind: 'error', text: 'Percentual inválido. Use o formato 2,99.' });
    setSaving(true);
    setMsg(null);
    try {
      const hoje = new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Bahia' }).format(new Date());
      await api.put('/settings/mp-fees', { taxaVendaBp: vendaBp, parcelamentoBp: parcBp, conferidaEm: hoje });
      setMsg({ kind: 'success', text: 'Taxas salvas. O simulador e as artes já usam os novos valores.' });
      onSaved();
    } catch (err) {
      setMsg({ kind: 'error', text: errorMessage(err) });
    } finally {
      setSaving(false);
    }
  }

  return (
    <form className="card narrow" onSubmit={(e) => void salvar(e)}>
      <h2>Tabela de taxas do Mercado Pago</h2>
      <p className="muted small">
        Confira no simulador do Mercado Pago (link de pagamento, cartão de crédito) e atualize quando mudar.
        {tabela.atualizadaPor && <> Última alteração por {tabela.atualizadaPor}.</>}
      </p>
      {msg && <Alert kind={msg.kind}>{msg.text}</Alert>}
      <div className="field">
        <label htmlFor="tx-venda">Taxa por venda (%)</label>
        <input id="tx-venda" inputMode="decimal" value={venda} onChange={(e) => setVenda(e.target.value)} />
      </div>
      <div className="fees-grid">
        {parc.map((v, i) =>
          i === 0 ? null : (
            <div className="field" key={i}>
              <label htmlFor={`tx-${i}`}>{i + 1}x (%)</label>
              <input id={`tx-${i}`} inputMode="decimal" value={v} onChange={(e) => setParc((p) => p.map((x, j) => (j === i ? e.target.value : x)))} />
            </div>
          ),
        )}
      </div>
      <Button type="submit" loading={saving}>
        Salvar taxas
      </Button>
    </form>
  );
}
